import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { RegexFormatter } from '@repo/toolkit/regex'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { InformationIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getUsers } from '../../../../api/user/get-users'
import {
  InsuranceTypeEnum,
  insuranceTypeEnumMap
} from '../../../../lib/types/enums/insurance/insurance-type.enum'
import { SuggestedInsuranceSelectionStatus } from '../../../../lib/types/enums/insurance/suggested-insurance-selection-status.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import {
  EnrollmentPropositionInput,
  InsuranceEnrollmentPropositionInput
} from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import {
  InsuranceLight,
  SuggestedInsuranceLight
} from '../../../../lib/types/models/insurance/insurance'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import InformationToolTip from '../../../elements/information-tooltip'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import EnrollmentPropositionSummary from './enrollment-proposition-summary'

type Props = {
  className?: string
  cseCode?: string | null
  disabled?: boolean
  defaultValues?: InsuranceEnrollmentPropositionInput[]
  disableTypes?: InsuranceTypeEnum[]
  insurances: SuggestedInsuranceLight[]
  isFetching?: boolean
  proposition?: EnrollmentPropositionInput
  onInsuranceChange: (insurances: InsuranceLight[], additionalEmails?: string[]) => Promise<void>
  onCancel: () => void
}

type InsuranceOption = {
  type: InsuranceTypeEnum
  label: string
  description?: string
  totalPrice: number
  isLocked: boolean
  insurances: SuggestedInsuranceLight[]
}

type InsuranceSelectionFormInput = {
  additionalEmails: string[]
}

const insuranceTypes = Object.values(InsuranceTypeEnum) as InsuranceTypeEnum[]

const uniqueInsuranceTypes = (types: InsuranceTypeEnum[]) => [...new Set(types)]

const isInsuranceLocked = (insurance: SuggestedInsuranceLight) =>
  insurance.selectionStatus !== SuggestedInsuranceSelectionStatus.OPTIONAL

export function EnrollmentPropositionInsuranceSelection({
  className,
  cseCode,
  disabled = false,
  defaultValues,
  disableTypes = [],
  insurances,
  isFetching,
  proposition,
  onInsuranceChange,
  onCancel
}: Props) {
  const [loading, setLoading] = useState(false)
  const form = useForm<InsuranceSelectionFormInput>({
    defaultValues: {
      additionalEmails: []
    }
  })

  const lockedTypes = useMemo(
    () =>
      uniqueInsuranceTypes(
        insurances.filter(isInsuranceLocked).map((insurance) => insurance.type)
      ),
    [insurances]
  )

  const defaultSelectedTypes = useMemo(
    () =>
      uniqueInsuranceTypes([
        ...(defaultValues?.map((insurance) => insurance.type) ?? []),
        ...lockedTypes
      ]),
    [defaultValues, lockedTypes]
  )

  const [selectedInsuranceTypes, setSelectedInsuranceTypes] =
    useState<InsuranceTypeEnum[]>(defaultSelectedTypes)

  useEffect(() => {
    setSelectedInsuranceTypes(defaultSelectedTypes)
  }, [defaultSelectedTypes])

  const insuranceOptions = useMemo<InsuranceOption[]>(() => {
    return insuranceTypes
      .map((insuranceType) => {
        const insurancesForType = insurances.filter(
          (insurance) => insurance.type === insuranceType
        )

        if (!insurancesForType.length) {
          return null
        }

        const totalPrice = insurancesForType.reduce((acc, insurance) => acc + insurance.price, 0)

        return {
          type: insuranceType,
          label: insuranceTypeEnumMap[insuranceType],
          description: insurancesForType.find((insurance) => insurance.description?.trim())
            ?.description,
          totalPrice,
          insurances: insurancesForType,
          isLocked:
            disabled ||
            disableTypes.includes(insuranceType) ||
            insurancesForType.some(isInsuranceLocked)
        }
      })
      .filter(Boolean) as InsuranceOption[]
  }, [disabled, disableTypes, insurances])

  const handleCheckboxChange = useCallback(
    (checked: boolean, insuranceType: InsuranceTypeEnum) => {
      setSelectedInsuranceTypes((prev) =>
        checked
          ? uniqueInsuranceTypes([...prev, insuranceType])
          : prev.filter((type) => type !== insuranceType)
      )
    },
    []
  )

  const selectedInsurances = useMemo(() => {
    const selectedTypes = uniqueInsuranceTypes([...selectedInsuranceTypes, ...lockedTypes])

    return insurances
      .filter((insurance) => selectedTypes.includes(insurance.type))
      .map((insurance) => ({ ...insurance, price: insurance.price }))
  }, [insurances, lockedTypes, selectedInsuranceTypes])

  const canShowCseSummary = !!cseCode && !!proposition

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true)

      await onInsuranceChange(
        selectedInsurances,
        canShowCseSummary ? form.getValues('additionalEmails') : undefined
      )
    } finally {
      setLoading(false)
    }
  }, [canShowCseSummary, form, onInsuranceChange, selectedInsurances])

  const { data: users } = useQuery({
    enabled: canShowCseSummary,
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } })
  })

  if (isFetching) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className={cn('flex flex-col gap-2', className)}>
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Options d'assurance
        </Typography>

        {insuranceOptions.map((option) => (
          <div key={option.type} className="flex flex-col gap-1">
            <div className={cn('flex items-center gap-2', { 'opacity-60': option.isLocked })}>
              <Checkbox
                label={option.label}
                checked={selectedInsuranceTypes.includes(option.type)}
                disabled={option.isLocked}
                wrapperClassName={cn({ 'pointer-events-none': option.isLocked })}
                onChange={(checked) => {
                  if (!option.isLocked) {
                    handleCheckboxChange(checked, option.type)
                  }
                }}
              />

              {option.description && (
                <InformationToolTip
                  align="center"
                  icon={
                    <InformationIcon
                      width="24px"
                      height="24px"
                      color={tailwindColors.secondary.DEFAULT}
                    />
                  }
                >
                  <Typography
                    variant="label-medium"
                    color={tailwindColors.white.DEFAULT}
                    className="w-56"
                  >
                    {option.description}
                  </Typography>
                </InformationToolTip>
              )}

              <Badge size="small" variant="primary" text={`${option.totalPrice}€`} />
            </div>

            <div className="ml-5 flex flex-wrap gap-2">
              {option.insurances.map((insurance) => (
                <Badge key={insurance.id} size="small" variant="grey" text={insurance.name} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {canShowCseSummary && (
        <div className="flex flex-col gap-4 px-5">
          <EnrollmentPropositionSummary
            className="shadow-none"
            input={{
              ...proposition,
              cseCode,
              insurances: selectedInsurances
            }}
            readonly
            onInputChange={() => Promise.resolve()}
          />

          <Form {...form}>
            <FormField
              control={form.control}
              name="additionalEmails"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      type="email"
                      label="Destinataires de la proposition"
                      searchable
                      allowNewOption
                      options={
                        users?.data.map((user) => ({
                          label: toFullName(user.firstName, user.lastName),
                          value: user.email
                        })) ?? []
                      }
                      selectedOptions={field.value?.map((email) => ({
                        label: email,
                        value: email
                      }))}
                      variant="warning"
                      onChange={(options) => {
                        field.onChange(
                          options.filter(
                            (email) => email !== '' && RegexFormatter.isValidEmail(email)
                          ) || []
                        )
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </Form>
        </div>
      )}

      <div className="mt-5 flex flex-row items-center justify-end gap-3 border-t border-t-white-active p-5">
        <Button
          variant="ghost"
          type="button"
          label="Fermer"
          aria-label="Fermer"
          onClick={onCancel}
        />

        <Button
          label="Valider"
          type="button"
          loading={loading}
          variant="primary"
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}
